import styled from "styled-components";
import { FC } from "react";
import { DateTime } from "luxon";

import Text from "@/components/Text";
import PostImage from "./PostImage";
import { FBImageDistributionResource } from "@/types/resources";
import GoToLink from "@/components/GoToLink";

const InfoCardBox = styled.div`
  margin: 16px 0;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
  border-radius: 4px;
  padding: 24px;
  position: relative;
  &:after {
    content: "";
    position: absolute;
    left: 50%;
    width: 1px;
    background-color: ${({ theme }) => theme.colors.lightGray};
    top: 24px;
    bottom: 24px;
  }
`;

const InfoCardGroupsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 48px;
  grid-row-gap: 24px;
`;

const InfoCardItem = styled.div`
  ${Text} {
    &:first-child {
      margin-bottom: 2px;
    }
  }
`;

const InfoCardItemImage = styled(InfoCardItem)`
  justify-self: left;
`;

const Image = styled.img`
  width: 16px;
  height: 16px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 6px;
  flex-shrink: 0;
  border: 1px solid ${({ theme }) => theme.colors.lightGray};
`;

const ChannelHolder = styled.span`
  display: flex;
  align-items: center;
`;

const Comment = styled(Text)`
  a {
    color: #008ef4;
    text-decoration: underline;
  }
`;

interface Props {
  post: FBImageDistributionResource;
}

const ContentCard: FC<Props> = ({ post }) => {
  return (
    <InfoCardBox>
      <InfoCardGroupsWrapper>
        <InfoCardItemImage>
          <Text $type="smallBold" $color="gray">
            Image
          </Text>
          <PostImage thumbnail={post.image?.versions?.original} />
        </InfoCardItemImage>
        <InfoCardItem>
          <Text $type="smallBold" $color="gray">
            Facebook Page
          </Text>
          {!!post.channel ? (
            <Text $type="caption">
              <ChannelHolder>
                {post.channel.thumbnail && (
                  <Image src={post.channel.thumbnail} alt="thumbnail" />
                )}
                {post.channel.name}
              </ChannelHolder>
            </Text>
          ) : (
            "-"
          )}
        </InfoCardItem>
        <InfoCardItem>
          <Text $type="smallBold" $color="gray">
            Share Message
          </Text>
          <Comment $type="caption">
            {post.message ? (
              post.comment ? (
                post.message
              ) : (
                <>
                  {post.message} <br />{" "}
                  {post.link ? (
                    <a
                      rel="noreferrer noopener"
                      target="_blank"
                      href={post.link}
                    >
                      {post.link}
                    </a>
                  ) : (
                    "-"
                  )}
                </>
              )
            ) : (
              "-"
            )}
          </Comment>
        </InfoCardItem>
        <InfoCardItem>
          <Text $type="smallBold" $color="gray">
            Comment Message
          </Text>
          <Comment $type="caption">
            {post.comment ? (
              <>
                {post.comment} <br />{" "}
                <a rel="noreferrer noopener" target="_blank" href={post.link}>
                  {post.link}
                </a>
              </>
            ) : (
              "-"
            )}
          </Comment>
        </InfoCardItem>
        <InfoCardItem>
          <Text $type="smallBold" $color="gray">
            Article link
          </Text>
          <Text $type="caption">
            {post.link ? <GoToLink link={post.link} /> : "-"}
          </Text>
        </InfoCardItem>
        <InfoCardItem>
          <Text $type="smallBold" $color="gray">
            Facebook Link
          </Text>
          <Text $type="caption">
            {post.facebookLink ? (
              <GoToLink
                onClick={(e) => e.stopPropagation()}
                link={post.facebookLink}
              />
            ) : (
              "-"
            )}
          </Text>
        </InfoCardItem>
        <InfoCardItem>
          <Text $type="smallBold" $color="gray">
            Post Date
          </Text>
          <Text $type="caption">
            {post.postDate
              ? DateTime.fromISO(post.postDate).toFormat("yyyy-LL-dd · HH:mm")
              : "-"}
          </Text>
        </InfoCardItem>
        <InfoCardItem>
          <Text $type="smallBold" $color="gray">
            Note (internal information)
          </Text>
          <Text $type="caption">{post.note ?? "-"}</Text>
        </InfoCardItem>
      </InfoCardGroupsWrapper>
    </InfoCardBox>
  );
};

export default ContentCard;
